import { useEffect, useState, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import { sanityFetch } from 'sanity-client';
import { useAtomValue, useSetAtom } from 'jotai';
import Image from 'next/image';
import { Typography, Button } from '@ag1/ui';
import { trueMedBannerLoad } from 'analytics/events/abTesting';
import { ContentPopup as ContentPopupProps, SanityKeyed } from 'types/studio/studio';
import { IsFromUs } from 'state/hooks/IsFromUs';
import { isPopupOpenState } from 'shared/studio/ContentPopup/atoms';
import { PopupVariant3 } from 'shared/studio/ContentPopup/Variant3/PopupVariant3';
import { quizPopupActionEvent as HSAPopupActionEvent } from 'analytics/events/quiz';
import clsx from 'clsx';
import styles from './HsaFsaAvailability.module.scss';

export const HsaFsaAvailability = ({
  sourcePage,
  isAsset = false
}: {
  sourcePage: 'checkout' | 'primaryLander';
  isAsset?: boolean;
}) => {
  const isUsStore = isAsset || IsFromUs();

  const truemedLoadEventFired = useRef(false);

  const [modalProps, setModalProps] = useState<SanityKeyed<ContentPopupProps> | null>(null);
  const { asPath } = useRouter();

  const popupActionEvent = useCallback(
    (detail: 'open' | 'close') => {
      HSAPopupActionEvent({
        detail,
        url: asPath,
        location: 'ContentPopup - Truemed'
      });
    },
    [asPath]
  );

  const setThirdVariationIsOpen = useSetAtom(isPopupOpenState);
  const isModalOpen = useAtomValue(isPopupOpenState);
  const closeModal = () => {
    setThirdVariationIsOpen(false);
    popupActionEvent('close');
  };
  const openModal = () => {
    setThirdVariationIsOpen(true);
    popupActionEvent('open');
  };

  useEffect(() => {
    const fetchMatchingContentPopup = async () => {
      const query = `*[_type == "sharedSection" && language == "en-us" && title == "Truemed"][0]{
        "matchingSection": sections[popupHandle == "truemed-hsa-fsa"]{
          ...,
          richTextField[]{ ... }
        }
      }`;

      try {
        const data = await sanityFetch(query);
        const popup = data?.matchingSection?.[0];
        if (popup) {
          setModalProps(popup);
        }
        return popup;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch matching contentPopup:', error);
        return null;
      }
    };

    fetchMatchingContentPopup();
  }, []);

  useEffect(() => {
    const locationText =
      sourcePage === 'checkout'
        ? 'Truemed prepurchase checkout'
        : 'Truemed prepurchase primary lander';

    if (isUsStore && !truemedLoadEventFired.current) {
      // ensure event is only fired once
      truemedLoadEventFired.current = true;
      trueMedBannerLoad({
        date: Date.now().toString(),
        location: locationText,
        shopify_id: '',
        test_variant: 'truemed-pre-purchase-direct'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUsStore]);

  if (!isUsStore) {
    return null;
  }

  return (
    <div className={clsx(styles.wrapper, isAsset && styles.assetWrapper)}>
      <Typography variant="body" size="1" className={styles.text} noPadding>
        HSA/FSA eligible with
      </Typography>
      <Image className={styles.img} src="/trumed_logo.png" alt="trumed" />
      <Button variant="linkGray" onClick={openModal} noPadding>
        <Typography
          variant="body"
          size="1"
          color="brand-green-new"
          className={styles.btnText}
          noPadding>
          Learn more
        </Typography>
      </Button>
      {modalProps && <PopupVariant3 {...modalProps} isOpen={isModalOpen} closePopup={closeModal} />}
    </div>
  );
};
